<template>
  <div class="wrapper">
    <b-form-input
      type="search"
      :class="inputClass"
      v-model="displayValue"
      autocomplete="off"
      @focus.native="onFocus"
      @blur="onBlur"
      @focus="focused = true"
      @keyup="handleKeyUp"
      :disabled="!enabled"
    />
    <div v-if="showDropdown" class="dropdown dropdown-menu">
      <b-dropdown-item-button
        :active="index === selectedItem"
        v-for="(item, index) in filteredItems"
        :key="`dropdown_item_${index}`"
        @click="onSelect(item.value)"
        tabindex="-1"
      >
        <div class="d-flex justify-content-space-between">
          <div class="flex-grow-1">
            {{ item.text }}
          </div>
          <i class="bi bi-check" v-if="item.selected"  />
        </div>
        <div v-if="item.subtitle" class="subtitle">
          {{ item.subtitle }}
        </div>
      </b-dropdown-item-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
// import debouce from 'lodash/debounce';

export interface AutocompleteItem {
  value: string;
  text: string;
  subtitle?: string;
  selected?: boolean;
}

export default defineComponent({
  props: {
    value: {
      type: String,
    },
    items: {
      type: Array as () => AutocompleteItem[],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
    },
    autoSelectSingle: {
      type: Boolean,
      default: false,
    },
    enabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      item: this.value,
      filter: '',
      displayValue:
        this.items?.find(item => item.value == this.value)?.text || this.value,
      innerValue: this.value as string | null,
      dropdown: false,
      focused: false,
      selectedItem: 0,
    };
  },
  methods: {
    setFilter(text: string) {
      const item = this.filteredItems[this.selectedItem];
      this.filter = text;
      this.selectedItem = this.filteredItems.indexOf(item);
    },
    onFocus() {
      if (!this.dropdown) {
        this.dropdown = true;
      }
    },
    onBlur(e) {
      // this.innerValue = this.displayValue;
      this.$emit('blur', e);
      setTimeout(() => {
        this.dropdown = false;
        this.focused = false;
      }, 200);
    },
    onSelect(value) {
      this.innerValue = value;
      this.setDisplayValue();
      this.setFilter('');
      Vue.nextTick(() => {
        this.dropdown = false;
      });
    },
    // changed: debouce(function(this: any) {
    //   this.$emit('input', this.innerValue);
    // }, 100),
    setDisplayValue() {
      if (!this.innerValue) {
        this.displayValue = '';
      } else {
        const item = this.items.find(item => item.value === this.innerValue);
        if (item) {
          this.displayValue = item.text;
        }
      }
    },
    handleKeyUp(event) {
      // Vue doesnt seem to like switch, so go for a safe if
      if (this.filteredItems?.length) {
        if (event.keyCode == 13) {
          this.innerValue = this.filteredItems[this.selectedItem]?.value;
          Vue.nextTick(() => {
            this.dropdown = false;
          });
        } else if (event.keyCode == 38) {
          if (!this.dropdown) {
            this.onFocus();
          } else if (0 < this.selectedItem) {
            this.selectedItem--;
          }
        } else if (event.keyCode == 40) {
          if (!this.dropdown) {
            this.onFocus();
          } else if (this.selectedItem < this.items.length - 1) {
            this.selectedItem++;
          }
        }
      }
    },
  },
  computed: {
    inputClass(): string {
      const classes: string[] = [];

      if (this.items?.length) {
        classes.push('custom-select');
      }

      if (this.size) {
        classes.push(' custom-select-' + this.size);
      }
      return classes.join(' ');
    },
    showDropdown(): boolean {
      return this.focused && this.dropdown && !!this.filteredItems?.length;
    },
    inputAttrs(): object {
      return {
        disabled: this.disabled,
        class: 'form-control form-control-sm',
      };
    },
    filteredItems(): AutocompleteItem[] {
      if (!this.filter) {
        return this.items;
      }

      return this.items.filter(item =>
        item.text.toLowerCase().includes(this.filter.toLowerCase())
      );
    },
  },
  watch: {
    value(val) {
      this.innerValue = val;
    },
    displayValue(val, oldVal) {
      if (oldVal && !val) {
        this.innerValue = null;
        this.setFilter('');
        // this.filter = val;
      } else if (oldVal != val && val != this.innerValue) {
        // this.setFilter(val);
        this.setFilter(val);
        this.onFocus();
      }
    },
    innerValue(val, oldVal) {
      this.$emit('input', val);
      if (val !== oldVal) {
        this.setDisplayValue();
        this.setFilter('');
        if (this.filteredItems?.length) {
          this.selectedItem = this.filteredItems.findIndex(
            item => item.value == val
          );
        }
      }
      // this.dropdown = false;
    },
    items(val, oldVal) {
      if (
        this.autoSelectSingle &&
        !this.innerValue &&
        this.items?.length == 1
      ) {
        this.innerValue = this.items[0].value;
      }

      if (val !== oldVal) {
        this.setDisplayValue();
        Vue.nextTick(() => {
          // Special case when items change, so we want to remove filter which
          // is otherwise set
          this.setFilter('');
        });
      }

      // If innerValue and displayValue is same check if there is a new display
      // value in items
      // if (this.innerValue === this.displayValue) {
      //   this.setDisplayValue();
      // }
    },
  },
});
</script>
<style scoped>
.wrapper {
  position: relative;
  flex: 1;
}

.dropdown {
  position: absolute;
  left: 0;
  width: 100%;
  display: block;
  border-radius: 0;

  width: unset;
  min-width: 100%;
}

.dropdown-item {
  overflow: hidden;
  text-overflow: ellipsis;
}

.subtitle {
  font-size: 0.75rem;
}
</style>
